import { isB2BUser, isSSR } from '@js/App/Utils/Environment'
import { useUser } from '@js/App/Api/User/User'

// Key under which we persist a not-yet-registered visitor's email for the
// duration of the browser session, so it can keep feeding the dataLayer on
// subsequent page visits before the visitor has an account.
const SESSION_STORAGE_KEY = 'rb_user_provided_email'

export const normalizeEmail = email => (email || '').trim().toLowerCase()

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const sha256Hex = async (value) => {
  const bytes = new TextEncoder().encode(value)
  const digest = await window.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

// Mirror usePostHog: only act once the visitor has granted marketing consent.
// A SHA256-hashed email is still personal data under the AVG, so the push is
// gated on consent rather than firing unconditionally.
const hasMarketingConsent = () => Boolean(window.Cookiebot?.consent?.marketing)

// Last hash pushed to the dataLayer this page-load. Guards against the duplicate
// pushes that otherwise stack up from the immediate call + CookiebotOnConsentReady
// + the initial navigate event all firing for the same email.
let lastPushedHash = null

const readStoredEmail = () => {
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY) || null
  } catch {
    return null
  }
}

// Authenticated email wins; otherwise fall back to a funnel-captured email
// persisted in sessionStorage for this anonymous visitor.
const resolveEmail = () => {
  const { user } = useUser()
  if (user.value.isLoggedIn && user.value.email) {
    return user.value.email
  }
  return readStoredEmail()
}

// Called as the visitor types their email in the create-account funnel, before
// they have an account. Persists it so pushUserProvidedData can pick it up on
// this and subsequent page visits.
export const captureUserProvidedEmail = (rawEmail) => {
  const normalized = normalizeEmail(rawEmail)
  if (!isValidEmail(normalized)) { return }
  // Don't persist the plaintext email without marketing consent — it's PII
  // under the AVG, same gate as the hashed dataLayer push below.
  if (!hasMarketingConsent()) { return }
  if (normalized === readStoredEmail()) { return }

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, normalized)
  } catch {
    // sessionStorage unavailable (private mode / blocked) — push live anyway.
  }
  pushUserProvidedData()
}

// Drop the persisted email once marketing consent is no longer granted, so a
// revocation doesn't leave plaintext PII sitting in sessionStorage.
export const clearStoredEmailWithoutConsent = () => {
  if (hasMarketingConsent()) { return }
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

export const pushUserProvidedData = async () => {
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!hasMarketingConsent()) { return }
  if (!window.dataLayer) { return }
  // crypto.subtle is only available in a secure context (HTTPS); bail rather
  // than throw an unhandled rejection on a non-TLS preview host.
  if (!window.crypto?.subtle) { return }

  const normalized = normalizeEmail(resolveEmail())
  if (!isValidEmail(normalized)) { return }

  try {
    const sha256EmailAddress = await sha256Hex(normalized)
    if (sha256EmailAddress === lastPushedHash) { return }
    lastPushedHash = sha256EmailAddress

    window.dataLayer.push({
      'user-provided-data': {
        sha256_email_address: sha256EmailAddress
      }
    })
  } catch (e) {
    // crypto.subtle.digest rejected — surface in dev, never throw an unhandled
    // rejection from the fire-and-forget call sites.
    console.warn('[Tracking 🕵️] pushUserProvidedData failed', e)
  }
}
